/**
 * TodoRow.jsx: one to-do on the To Do page. Checkbox, the $ mark (MoneyMark:
 * faint grey, gold when marked), title, then a meta line: due label (red when
 * overdue, bold when due today), the link chip, "From <name>" when someone else
 * assigned it to me, and the assignee in the Team view. Clicking the row opens
 * the dialog; the checkbox and the $ only do their own thing.
 *
 * SortableTodoRow (open rows in Mine) adds a grip on the far right, so the left
 * side stays checkbox, $, title on a phone. Only the grip starts a drag, and only
 * the grip has touch-action: none, so a swipe anywhere else on the row still
 * scrolls the page and a tap still opens the to-do.
 *
 * The row shows to-do and record text, so the row, checkbox, $, grip and chip
 * carry static data-track names (todos.row, todos.done, todos.money, todos.drag,
 * todos.link).
 */
import { Box, Checkbox, Typography } from '@mui/material';
import { DragIndicator as DragIcon } from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { fmtDue, dueTone } from '../../lib/todoText';
import TodoLinkChip from './TodoLinkChip';
import MoneyMark from './MoneyMark';

const TONE_SX = {
  overdue: { color: 'error.main', fontWeight: 600 },
  today: { color: 'text.primary', fontWeight: 700 },
  soon: { color: 'text.primary' },
  later: { color: 'text.secondary' },
  none: {},
};

/** The grip, at the far right. 44px wide on phones; narrow and faint on desktop until the row is hovered. */
function DragHandle({ sortable }) {
  return (
    <Box
      component="button"
      type="button"
      ref={sortable.setActivatorNodeRef}
      {...sortable.attributes}
      {...sortable.listeners}
      aria-label="Move"
      data-track="todos.drag"
      className="todo-drag-handle"
      onClick={(e) => e.stopPropagation()}
      sx={{
        flexShrink: 0,
        alignSelf: 'stretch',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        width: { xs: 44, sm: 24 },
        minHeight: 44,
        my: -0.5,
        // The whole row height is the target; the icon sits level with the checkbox.
        p: 0,
        pt: { xs: 1.75, sm: 1.5 },
        border: 0,
        borderRadius: 1,
        bgcolor: 'transparent',
        color: { xs: 'text.secondary', sm: 'text.disabled' },
        cursor: sortable.isDragging ? 'grabbing' : 'grab',
        touchAction: 'none',
        WebkitTapHighlightColor: 'transparent',
        '&:focus-visible': { outline: 2, outlineColor: 'primary.main', color: 'text.primary' },
      }}
    >
      <DragIcon sx={{ fontSize: 20 }} />
    </Box>
  );
}

export default function TodoRow({ todo, today, meId, showAssignee, onToggle, onMoney, onOpen, sortable }) {
  const t = todo;
  const tone = t.done ? 'later' : dueTone(t.dueDate, today);
  const due = fmtDue(t.dueDate, today);
  const fromOther = !t.isPrivate && t.createdBy?.name && t.createdBy.id !== t.assignee?.id && t.assignee?.id === meId;
  const dragging = !!sortable?.isDragging;

  return (
    <Box
      ref={sortable?.setNodeRef}
      style={sortable?.style}
      data-track="todos.row"
      onClick={() => onOpen(t)}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.5,
        minHeight: 44,
        py: 0.5,
        pr: sortable ? 0 : 1,
        borderLeft: 3,
        borderColor: tone === 'overdue' ? 'error.main' : 'transparent',
        borderRadius: 1,
        cursor: 'pointer',
        bgcolor: dragging ? 'background.paper' : undefined,
        boxShadow: dragging ? 4 : 'none',
        zIndex: dragging ? 1 : 'auto',
        '&:hover': { bgcolor: dragging ? 'background.paper' : 'action.hover' },
        '&:hover .todo-drag-handle': { color: 'text.secondary' },
      }}
    >
      <Checkbox
        data-track="todos.done"
        checked={!!t.done}
        onClick={(e) => e.stopPropagation()}
        onChange={() => onToggle(t)}
        slotProps={{ input: { 'aria-label': t.done ? 'Mark not done' : 'Mark done' } }}
        sx={{ p: { xs: 1.25, sm: 0.75 }, mt: { xs: -0.25, sm: 0 } }}
      />
      <MoneyMark
        marked={!!t.money}
        onToggle={() => onMoney(t)}
        trackKey="todos.money"
        sx={{ mt: { xs: -0.25, sm: 0 }, ml: { xs: -0.5, sm: -0.25 } }}
      />
      <Box sx={{ flex: 1, minWidth: 0, pt: { xs: 1, sm: 0.75 } }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            lineHeight: 1.35,
            overflowWrap: 'anywhere',
            textDecoration: t.done ? 'line-through' : 'none',
            color: t.done ? 'text.secondary' : 'text.primary',
          }}
        >
          {t.title}
        </Typography>
        {(due || t.link || fromOther || showAssignee) && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1, rowGap: 0.5, mt: 0.25 }}>
            {due && <Typography variant="caption" sx={TONE_SX[tone]}>{due}</Typography>}
            {t.link && <TodoLinkChip link={t.link} />}
            {fromOther && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>From {t.createdBy.name}</Typography>
            )}
            {showAssignee && (
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                {t.assignee?.name || 'No one'}
              </Typography>
            )}
          </Box>
        )}
      </Box>
      {sortable && <DragHandle sortable={sortable} />}
    </Box>
  );
}

/** An open row in Mine: draggable by its grip, inside the page's DndContext + SortableContext. */
export function SortableTodoRow(props) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: props.todo.id });
  const style = {
    // Up and down only, and no scaling between rows of different heights.
    transform: CSS.Translate.toString(transform && { ...transform, x: 0 }),
    transition,
  };
  return (
    <TodoRow
      {...props}
      sortable={{ attributes, listeners, setNodeRef, setActivatorNodeRef, style, isDragging }}
    />
  );
}
